import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download } from 'lucide-react';
import { api, API_BASE, ApiError } from '@/lib/api';
import { normalizeDatasetDetail } from '@/lib/normalize';
import type { DatasetDecision, Episode, EvidenceFile, Gate } from '@/lib/contract';
import {
  collectionLabel,
  formatBytes,
  formatDay,
  formatDuration,
  formatHours,
  modalityLine,
  orDash
} from '@/lib/format';
import { PageHeader } from '@/ui/page-header';
import { Panel } from '@/ui/panel';
import { Button } from '@/ui/button';
import { Badge } from '@/ui/badge';
import { MicroLabel } from '@/ui/micro-label';
import { StatTile } from '@/ui/stat-tile';
import { DataTable, type Column } from '@/ui/data-table';
import { GateBadge } from '@/domain/gate-badge';
import { VersionLineage } from '@/domain/version-lineage';
import { QaState } from '@/domain/qa-state';
import { QualityReportPanel } from '@/domain/quality-report';
import { EpisodeViewer } from '@/domain/episode-viewer';
import { cn } from '@/lib/cn';
import { ErrorState } from '@/ui/error-state';
import { Tooltip } from '@/ui/tooltip';
import { useRealtimeRefresh } from '@/lib/realtime';

const episodeColumns: Column<Episode>[] = [
  {
    key: 'code',
    header: 'Episode',
    className: 'w-[90px]',
    render: e => <span className="font-mono text-[12px] text-foreground">{e.code}</span>
  },
  {
    key: 'dur',
    header: 'Dur',
    className: 'w-[60px]',
    render: e => <span className="font-mono text-[12px] text-muted">{formatDuration(e.duration_seconds)}</span>
  },
  { key: 'task', header: 'Task', render: e => <span className="text-[12px]">{e.task}</span> },
  {
    key: 'annot',
    header: 'Annot',
    className: 'w-[60px]',
    // An em dash, matching the Flags column beside it: this episode carries no annotations, which
    // is a fact worth showing rather than a blank worth wondering about.
    render: e =>
      e.annotation_level ? (
        <span className="font-mono text-[11px] text-muted">{e.annotation_level}</span>
      ) : (
        <span className="text-dim">—</span>
      )
  },
  {
    key: 'qa',
    header: 'QA state',
    className: 'w-[116px] whitespace-nowrap',
    render: e => <QaState state={e.qa_state} />
  },
  {
    key: 'flags',
    header: 'Flags',
    className: 'w-[70px]',
    render: e =>
      e.flag_ref ? (
        <span className="font-mono text-[11px] text-primary">{e.flag_ref}</span>
      ) : (
        <span className="text-dim">—</span>
      )
  }
];

export function DatasetDetailScreen() {
  const { id = '' } = useParams();
  const [params] = useSearchParams();
  const tenantId = params.get('tenant') ?? undefined;
  const queryClient = useQueryClient();
  // Which episode the viewer is showing. The table used to swallow its own row clicks, so the
  // viewer was permanently pinned to whichever episode happened to be first.
  const [episode, setEpisode] = useState<Episode | null>(null);
  const { data, isPending, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'dataset', id, tenantId],
    queryFn: async () => {
      return normalizeDatasetDetail((await api(`/datasets/${id}`, { tenantId })) as Record<string, unknown>);
    }
  });

  /**
   * `POST :id/black-box-run/rerun` already existed with the `dataset.evaluate` permission; the
   * button simply was not connected to it. Admission is a lock-and-insert, so a double click
   * cannot start two runs -- but the button disables itself anyway rather than relying on that.
   */
  useRealtimeRefresh(`/datasets/${id}/black-box-run/subscription-token`, [['admin', 'dataset', id, tenantId]], {
    realm: 'admin'
  });

  const rerun = useMutation({
    mutationFn: () => api(`/datasets/${id}/black-box-run/rerun`, { method: 'POST', tenantId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'dataset', id, tenantId] })
  });

  // `if (!data) return null` rendered a blank page for a failed fetch, an unknown id and a slow
  // network alike. Each is now distinguishable.
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} title="Could not load this dataset" />;
  if (isPending || !data) return <DetailSkeleton />;

  const specLine = [
    modalityLine(data.modalities),
    collectionLabel(data.collection_type),
    data.rig,
    data.format_label,
    data.intake_spec
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="grid gap-4">
      <nav className="flex items-center gap-2">
        <Link
          to="/admin/datasets"
          className="font-mono text-[10px] tracking-[0.12em] text-dim uppercase hover:text-muted"
        >
          Datasets
        </Link>
        <span className="text-dim">/</span>
        <MicroLabel size="xs" caps={false}>
          {data.name}
        </MicroLabel>
      </nav>

      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {data.name}
            <span className="font-mono text-[13px] text-muted">{data.version_label}</span>
            {data.certified_at ? <Badge tone="pass">Certified</Badge> : null}
            {data.gate ? <GateBadge gate={data.gate} /> : null}
            <MicroLabel size="xs">{data.vendor_name}</MicroLabel>
          </span>
        }
        subtitle={specLine}
        actions={
          <>
            {/* No delivery-packaging endpoint exists. A button that looks live and does nothing is
                worse than one that says why it cannot. */}
            <Tooltip label="Delivery packaging is not available in this build">
              <span>
                <Button disabled aria-disabled className="cursor-not-allowed opacity-60">
                  Package for delivery
                </Button>
              </span>
            </Tooltip>
            <Button variant="secondary" onClick={() => rerun.mutate()} disabled={rerun.isPending}>
              {rerun.isPending ? 'Re-evaluating…' : 'Re-evaluate'}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-5 gap-4 max-lg:grid-cols-3 max-sm:grid-cols-1">
        <StatTile
          label="Hours"
          value={formatHours(data.hours)}
          foot={data.episode_count ? `${data.episode_count} episodes` : undefined}
        />
        <StatTile label="Size" value={formatBytes(data.size_bytes)} foot="sha256 verified" footTone="pass" />
        <StatTile
          label="Quality gate"
          value={<GateBadge gate={data.gate} className="text-[30px]" />}
          foot={
            data.certified_at
              ? `${data.version_label} · certified ${formatDay(data.certified_at)}`
              : data.quality_score !== null
                ? `${data.version_label} · score ${data.quality_score}`
                : orDash(null)
          }
          footTone={data.certified_at ? 'pass' : 'muted'}
        />
        <StatTile
          label="Flags"
          value={`${data.open_flag_count} open`}
          foot={(() => {
            const resolved = data.flags.filter(f => f.state === 'resolved');
            if (!resolved.length) return `${data.flags.length} raised in total`;
            const version = resolved.find(f => f.resolved_in_version)?.resolved_in_version;
            return `${resolved.length} resolved${version ? ` in v${version}` : ''}`;
          })()}
        />
        <StatTile label="Deliveries" value={data.delivery_refs.length} foot={data.delivery_refs[0]} />
      </div>

      <Panel title="Version lineage">
        <VersionLineage nodes={data.lineage} />
      </Panel>

      <div className="grid grid-cols-[1fr_1fr] gap-4 max-xl:grid-cols-1">
        <Panel
          title="Episodes"
          subtitle={
            data.episode_count === null
              ? 'no episode metadata in this upload'
              : `${data.episode_count} episodes · click row to load in viewer`
          }
          bodyClassName="px-0 pb-0"
        >
          <DataTable
            columns={episodeColumns}
            rows={data.episodes}
            rowKey={row => row.id}
            onRowClick={row => setEpisode(row)}
            className="rounded-none border-0 bg-transparent"
            // Roughly the height of the viewer beside it, so the pair reads as one row rather than
            // sixty episodes pushing the decision panel off the bottom of the page.
            maxBodyHeight={420}
            footer={<MicroLabel size="xs">QA states reflect bounded sample · not full-census QA</MicroLabel>}
          />
        </Panel>

        <EpisodeViewer datasetId={id} tenantId={tenantId} episode={episode ?? data.episodes[0]} />
      </div>

      <DecisionPanel
        datasetId={id}
        tenantId={tenantId}
        decision={data.decision}
        lifecycleStatus={data.lifecycle_status}
        gate={data.gate}
        blockingFlags={data.flags.filter(f => f.state === 'open' && f.blocking).length}
      />

      <div className="grid grid-cols-[1fr_1fr] gap-4 max-xl:grid-cols-1">
        <QualityReportPanel report={data.quality_report} flags={data.flags} />
        <EvidencePanel files={data.evidence} version={data.version_label ?? ''} datasetId={id} />
      </div>
    </div>
  );
}

/**
 * The operator's decision on a settled run.
 *
 * `POST :id/approve` and `:id/reject` have existed and been permissioned since the first pass, and
 * nothing in the console called either -- so every evaluated dataset stopped at *Decision required*
 * and stayed there for ever. This is the control that ends the run.
 *
 * A rejection takes a reason and will not submit without one. The note is the only part of this the
 * vendor sees, and "rejected" with nothing beside it tells them nothing they can act on.
 */
function DecisionPanel({
  datasetId,
  tenantId,
  decision,
  lifecycleStatus,
  gate,
  blockingFlags
}: {
  datasetId: string;
  tenantId?: string;
  decision: DatasetDecision | null;
  lifecycleStatus: string;
  gate: Gate;
  blockingFlags: number;
}) {
  const queryClient = useQueryClient();
  const [note, setNote] = useState('');
  const [error, setError] = useState('');

  const decide = useMutation({
    mutationFn: (outcome: 'approve' | 'reject') =>
      api(`/datasets/${datasetId}/${outcome}`, {
        method: 'POST',
        tenantId,
        body: JSON.stringify({ note: note.trim() || null })
      }),
    onSuccess: () => {
      setError('');
      setNote('');
      void queryClient.invalidateQueries({ queryKey: ['admin', 'dataset', datasetId, tenantId] });
    },
    onError: cause => setError(cause instanceof ApiError ? cause.message : 'Could not record the decision.')
  });

  if (decision)
    return (
      <Panel
        title="Decision"
        action={<Badge tone={decision.decision === 'approved' ? 'pass' : 'danger'}>{decision.decision}</Badge>}
        subtitle={`Recorded ${formatDay(decision.decided_at)}`}
      >
        <p className="text-[13px] leading-relaxed text-foreground">
          {decision.note || <span className="text-dim">No note was recorded with this decision.</span>}
        </p>
      </Panel>
    );

  // Only a settled run can be decided on -- `decide` attaches the decision to one, and there is
  // nothing to attach to while the evaluation is still running or has not started.
  /**
   * One state, not a whitelist of three.
   *
   * This named `decision_required | needs_fixes | evaluated` and so silently excluded `in_review` --
   * a conditionally-gated run derived that, and the operator got no Approve or Reject control at
   * all. Now every settled run derives `decision_required`, whatever its gate, so the queue is a
   * single value and there is nothing to leave out.
   */
  if (lifecycleStatus !== 'decision_required') return null;

  return (
    <Panel
      title="Decision"
      subtitle="Approve or reject this evaluation. The note is shown to the vendor."
      action={gate ? <GateBadge gate={gate} /> : null}
    >
      {blockingFlags > 0 ? (
        <p className="mb-3 text-[13px] text-danger">
          {blockingFlags} blocking flag{blockingFlags === 1 ? '' : 's'} still open on this dataset.
        </p>
      ) : null}
      <textarea
        value={note}
        onChange={event => setNote(event.target.value)}
        rows={3}
        placeholder="What the vendor needs to know about this outcome…"
        className="w-full rounded border border-border bg-inset px-3 py-2 text-[13px] text-foreground placeholder:text-dim"
      />
      {error ? <p className="mt-2 text-[13px] text-danger">{error}</p> : null}
      <div className="mt-3 flex items-center gap-2">
        <Button disabled={decide.isPending} onClick={() => decide.mutate('approve')}>
          {decide.isPending && decide.variables === 'approve' ? 'Approving…' : 'Approve'}
        </Button>
        <Button
          variant="danger"
          // A rejection without a reason is not actionable by the person receiving it.
          disabled={decide.isPending || !note.trim()}
          title={note.trim() ? undefined : 'A rejection needs a reason'}
          onClick={() => decide.mutate('reject')}
        >
          {decide.isPending && decide.variables === 'reject' ? 'Rejecting…' : 'Reject'}
        </Button>
      </div>
    </Panel>
  );
}

function EvidencePanel({ files, version, datasetId }: { files: EvidenceFile[]; version: string; datasetId: string }) {
  /**
   * A plain link, not a fetch: the server streams the zip and names it in `Content-Disposition`, so
   * the browser writes it straight to disk instead of us buffering a whole archive in memory to
   * hand back a Blob. The session cookie rides along because this is the same origin.
   *
   * No tenant is passed. A link cannot set the `x-tenant-id` header the admin routes read, and it
   * does not need to: like every other operator route here, the handler resolves the tenant from
   * the dataset row (`tenantOf`).
   */
  const href = `${API_BASE}/admin/datasets/${datasetId}/evidence/pack`;
  return (
    <Panel
      title={`Evidence pack · ${version}`}
      subtitle="Sha-pinned · signed · reproducible"
      action={
        files.length ? (
          <Button size="sm" asChild>
            <a href={href}>
              <Download className="size-3.5" /> Download pack
            </a>
          </Button>
        ) : (
          // Said rather than hidden: no evidence before a completed run is a stage, not a fault.
          <Tooltip label="No evidence pack exists until a QA run completes">
            <span>
              <Button size="sm" disabled aria-disabled className="cursor-not-allowed opacity-60">
                Download pack
              </Button>
            </span>
          </Tooltip>
        )
      }
    >
      <ul className="grid">
        {files.map((file, index) => (
          <li
            key={file.name}
            className={cn(
              'grid grid-cols-[150px_70px_80px_1fr] items-center gap-3 py-2.5',
              index > 0 && 'border-t border-border'
            )}
          >
            <span className="rounded-[3px] bg-inset px-2 py-1 font-mono text-[11px] text-foreground">{file.name}</span>
            <span className="font-mono text-[11px] text-muted">{formatBytes(file.size_bytes)}</span>
            <span className="font-mono text-[11px] text-dim">{orDash(file.checksum_prefix)}</span>
            <span className="font-mono text-[11px] text-muted">{file.description}</span>
          </li>
        ))}
      </ul>
      <MicroLabel size="xs" className="mt-4 block leading-relaxed">
        No intermediate artifacts · evaluation internals stay inside the factory boundary
      </MicroLabel>
    </Panel>
  );
}

/** Deliberately quiet: the detail page is dense, and a skeleton of it would be noisier than this. */
function DetailSkeleton() {
  return (
    <div className="grid place-items-center rounded border border-border bg-card px-6 py-20">
      <MicroLabel size="xs">Loading dataset</MicroLabel>
    </div>
  );
}
